import { WebResourceLock, LockStrategy, ClientResourceLock } from '@tencent/ark-lock';
import ua from '@tencent/docs-user-agent';
import { MutexLockState, MutexLockSource } from '../types/mutex-lock-helper.interface';
import type { IMutexLockHelper, DetectLockSuccessCallBack } from '../types/mutex-lock-helper.interface';
import type { ILogger } from '@tencent/slide-sdk-model/src/manager/logger.interface';
import type { ILock, ILockConfig, ILockItem, ILockResult } from '@tencent/ark-lock';

/**
 * 上锁失败等待间隔
 */
const lockWaitTingInterval = 30 * 1000;

/**
 * 互斥锁默认配置
 */
const defaultLockConfig: ILockConfig = {
    strategy: LockStrategy.ONCE,
    // 1秒过期
    period: 1 * 1000,
    // 自动续期
    autoRenewal: true,
};

export class MutexLockHelper implements IMutexLockHelper {
    public lastLockItem?: ILockItem;
    private readonly tag = '[MLH]';
    // 单页面内互斥锁
    private mutexLock: ILock;
    // 用加密id生成key
    private readonly lockKey: string;
    private detectTimer: NodeJS.Timeout;
    private lockState: MutexLockState = MutexLockState.INIT;
    // 连续 lock 请求场景下使用promise复用请求，同一时刻只有一个真实的请求
    private lockPromise?: Promise<boolean>;

    constructor(readonly secretId: string, private logger: ILogger) {
        this.mutexLock = ua.isSupportNativeDatabase
            ? new ClientResourceLock(defaultLockConfig)
            : new WebResourceLock(defaultLockConfig);
        this.lockKey = `offline_edit_${this.secretId}`;
    }

    public async dispose(): Promise<boolean> {
        clearInterval(this.detectTimer);
        await this.release();
        return true;
    }

    public async lock(): Promise<boolean> {
        if (!this.lockPromise) {
            this.lockPromise = new Promise<boolean>((resolve) => {
                this.mutexLock.lock(this.lockKey, { source: MutexLockSource.EDIT }).then((result: ILockResult) => {
                    this.lastLockItem = undefined;
                    if (result?.isSuccess) {
                        this.setLockState(MutexLockState.SUCCESS);
                    } else {
                        this.lastLockItem = result.data;
                        this.setLockState(MutexLockState.FAILED);
                        // 上锁失败有限制频率，限频期间不会重新请求锁，上锁失败有个等待区间
                        const delaySetLockFail = setTimeout(() => {
                            this.reset();
                            clearTimeout(delaySetLockFail);
                        }, lockWaitTingInterval);
                    }
                    this.logger.info(this.tag, `lock result: ${result?.isSuccess} ${result?.data?.id}`);
                    resolve(this.isLocked());
                });
            });
        }

        return this.lockPromise;
    }

    public isLocked(): boolean {
        return this.lockState === MutexLockState.SUCCESS;
    }

    public isLockedByOtherPage(): boolean {
        return !this.isLocked() && this.lastLockItem?.optional?.source === MutexLockSource.EDIT;
    }

    /**
     * 连续探测锁
     *
     * @param {DetectLockSuccessCallBack} detectLockCb
     * @param {number} [interval=200]
     */
    public detect(detectLockCb: DetectLockSuccessCallBack, interval = 200): void {
        this.logger.info(this.tag, `detect lock, interval=${interval}`);
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        this.detectTimer = setInterval(async () => {
            const lockResult = await this.mutexLock.detectKey(this.lockKey);
            if (!lockResult.error && lockResult.data === undefined) {
                // 没有发生异常  而且检测到没有其他标签页占用锁了 就可以刷新了
                detectLockCb();
                clearInterval(this.detectTimer);
                return;
            }
            if (lockResult.data) {
                this.lastLockItem = lockResult.data;
            }
        }, interval);
    }

    /**
     * 释放锁
     *
     * @return {*}  {Promise<boolean>}
     */
    public async release(): Promise<boolean> {
        const releaseResult = await this.mutexLock.release(this.lockKey);
        this.logger.info(this.tag, `release lock: ${releaseResult}`);
        this.setLockState(MutexLockState.RELEASE);
        this.reset();
        return true;
    }

    public getLockState(): MutexLockState {
        return this.lockState;
    }

    private setLockState(state: MutexLockState) {
        if (state !== this.lockState) {
            this.lockState = state;
        }
    }

    private reset(): void {
        this.lockPromise = undefined;
    }
}
